<script>
import { MODOS_AGRUPAMENTO } from '@modules/aawz-intelligence-v2/constants/TextChat'

// Tamanho do ícone do botão de modo — casa com o rótulo de 12px.
const TAMANHO_ICONE = 14
// Ícone do modo ativo escurece junto com o rótulo. Nunca cor de marca: o verde
// dá 2.43 sobre o fundo quente (ver `_controls.sass`).
const COR_ICONE_ATIVO = 'var(--neutral-900)'
const COR_ICONE_PADRAO = 'var(--neutral-700)'

export default {
  name: 'IntelligenceV2ChatGroupModeTabs',
  props: {
    // Catálogo dos modos. Prop (e não import direto) para o teste poder provar
    // que o segmented é data-driven sem mutar a constante.
    modos: {
      type: Array,
      default: function () {
        return MODOS_AGRUPAMENTO
      },
    },
    modoAtivo: {
      type: String,
      default: '',
    },
  },
  computed: {
    // Tudo o que o template consome sai pronto daqui: o `<template>` só lê
    // propriedade. `bloqueado` é DERIVADO de `disabledReason` — um campo
    // próprio abriria a porta para desabilitar sem motivo, que na tela é
    // indistinguível de bug.
    itens() {
      const ativoId = this.modoAtivo
      return this.modos.map(function (modo) {
        const bloqueado = Boolean(modo.disabledReason)
        const ativo = !bloqueado && modo.id === ativoId
        return {
          id: modo.id,
          label: modo.label,
          iconName: modo.iconName,
          bloqueado: bloqueado,
          motivo: modo.disabledReason || '',
          semMotivo: !modo.disabledReason,
          ativo: ativo,
          ariaPressed: ativo ? 'true' : 'false',
          corIcone: ativo ? COR_ICONE_ATIVO : COR_ICONE_PADRAO,
        }
      })
    },
    tamanhoIcone() {
      return TAMANHO_ICONE
    },
  },
  methods: {
    // O `:disabled` do <button> já barra o clique nativo; o guard aqui existe
    // porque o <span> do tooltip envolve o botão e pode receber o evento por
    // ele. Clicar no modo já ativo também não emite: nada mudou.
    selecionar(item) {
      if (item.bloqueado || item.ativo) return
      this.$emit('change', item.id)
    },
  },
}
</script>

<template>
  <!-- Sem `role` de grupo: ele não tinha nome acessível, então o leitor de tela
       anunciava "grupo" sem dizer qual — ruído, não ajuda. Quem comunica o
       estado é o `aria-pressed` de cada botão. -->
  <div class="chat-group-mode-tabs">
    <!-- `right` e não `bottom`: o motivo do modo desabilitado é uma frase, e
         dentro dos 288px da sidebar ela quebraria em várias linhas por cima do
         próprio segmented. À direita, abre sobre a área do chat. -->
    <v-tooltip
      v-for="item in itens"
      :key="item.id"
      right
      :disabled="item.semMotivo"
    >
      <template v-slot:activator="{ on, attrs }">
        <span class="chat-group-mode-tabs__slot" v-bind="attrs" v-on="on">
          <!-- O hook `__botao` acompanha a anatomia compartilhada: quando o
               botão migrou para `.ia-sidebar-row` a classe do componente saiu
               junto, e os testes deste arquivo — que ancoram nela, e não numa
               utilitária compartilhada — pararam de achar os botões. -->
          <button
            type="button"
            class="chat-group-mode-tabs__botao ia-sidebar-row ia-sidebar-row--compacto"
            :class="{ 'ia-sidebar-row--ativo': item.ativo }"
            :disabled="item.bloqueado"
            :aria-pressed="item.ariaPressed"
            @click="selecionar(item)"
          >
            <icon-svg
              :name="item.iconName"
              :size="tamanhoIcone"
              :color="item.corIcone"
              class="flex-shrink-0"
            />
            <span class="text-truncate">{{ item.label }}</span>
          </button>
        </span>
      </template>
      <span>{{ item.motivo }}</span>
    </v-tooltip>
  </div>
</template>

<style lang="sass" scoped>
// Duas colunas e não quatro em linha: "Profissional" a 12px não cabe num
// quarto de 288px sem truncar para "Profis...".
.chat-group-mode-tabs
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 2px

  // O `v-tooltip` embrulha o ativador num `span.v-tooltip` — é ELE que vira o
  // item do grid, e sendo inline o grid de 2 colunas não pega. `display:
  // contents` tira o wrapper do layout sem afetar o overlay, que o Vuetify
  // posiciona pelo ativador real. Precedente no repo: suggestion-card.vue e
  // chat-message-actions.vue (ver CLAUDE.md § Ativador de v-tooltip em flex).
  ::v-deep .v-tooltip
    display: contents

.chat-group-mode-tabs__slot
  display: block
  min-width: 0

// A anatomia inteira (padding, raio, hover, cores, estado ativo E o passo de
// 12px) vem de `.ia-sidebar-row--compacto` (design/_ia-sidebar.sass): a
// exceção que autoriza tamanhos fora da escala do DS exige que não sejam
// duplicados em `<style scoped>`, então aqui fica só o grid.
</style>
